import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Paper,
  Autocomplete, Pagination,
  Fab, Tooltip,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Avatar,
  Divider,
  useTheme
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Helmet } from "react-helmet";
import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import DraftsIcon from "@mui/icons-material/Drafts";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArchiveManager from "../utils/archiveManager";
import ConfirmDialog from "../components/ConfirmDialog";
import SettingsIcon from "@mui/icons-material/Settings";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";

function StatCard({ label, count, IconComponent, active = false, onClick }) {
  return (
    <Card
      onClick={onClick}
      variant="outlined"
      sx={{
        height: "100%",
        minWidth: 0,
        width: "100%",
        borderRadius: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        background: active
          ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.28) 0%, rgba(44, 62, 80, 0.5) 100%)'
          : 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        border: active
          ? '2px solid #4CA1AF'
          : '1px solid rgba(255,255,255,0.1)',
        boxShadow: active
          ? '0 8px 24px rgba(76, 161, 175, 0.35)'
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-3px)',
          borderColor: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.5)',
          background: active
            ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.35) 0%, rgba(44, 62, 80, 0.6) 100%)'
            : 'rgba(76, 161, 175, 0.12)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
        } : {},
      }}
    >
      <CardContent sx={{ minHeight: 80, p: '16px !important' }}>
        <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              height: 48,
              width: 48,
              background: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.2)',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              flexShrink: 0,
              transition: 'all 0.25s ease',
              boxShadow: active ? '0 4px 12px rgba(76, 161, 175, 0.4)' : 'none',
            }}
          >
            <IconComponent sx={{ fontSize: 28, color: active ? '#FFFFFF' : '#4CA1AF' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: '#FFFFFF' }}>
                {count}
              </Typography>
              {active && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: 'rgba(76, 161, 175, 0.3)',
                    border: '1px solid rgba(76, 161, 175, 0.6)',
                    color: '#64d2e2',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Active
                </Box>
              )}
            </Box>
            <Typography variant="body2" noWrap sx={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)', fontWeight: active ? 600 : 400 }}>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Testimonies() {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // Form states
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [requestText, setRequestText] = useState("");
  const [formError, setFormError] = useState("");

  // Modal and snackbar states
  const [openDialog, setOpenDialog] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [selectedTestimony, setSelectedTestimony] = useState(null);

  // Filter states
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSort, setFilterSort] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [organizations, setOrganizations] = useState([]);
  const [myPrayers, setMyPrayers] = useState([]);


  const [organization, setOrganization] = useState(null);
  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];
  const [page, setPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [publicPrayerRequests, setPublicPrayerRequests] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [myPrayer, setMyPrayer] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [created_at, setCreated_at] = useState("");
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const { user, isSwitched, effectiveOrgId } = useUser();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [ActivePrayers, SetActivePrayers] = useState(0);
  const [PendingPrayers, SetPendingPrayers] = useState(0);
  const [DeclinePrayers, SetADeclinePrayers] = useState(0);

  const [editTestimonyId, setEditTestimonyId] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';

  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('testimonies'));
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('testimonies'));
  const [filterActiveTab, setFilterActiveTab] = useState('all');

  // Auto-save draft on beforeunload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if ((title && title.trim()) || (requestText && requestText.trim())) {
        DraftManager.saveDraft('testimonies', {
          title, description: requestText, myPrayer
        }, activeDraftId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [title, requestText, myPrayer, activeDraftId]);

  const handleManualSaveDraft = () => {
    if (!title.trim() && !requestText.trim()) {
      setToast({ open: true, message: "Please enter a title or testimony before saving draft.", severity: "warning" });
      return;
    }
    const res = DraftManager.saveDraft('testimonies', {
      title, description: requestText, myPrayer
    }, activeDraftId);

    if (res.success) {
      setActiveDraftId(res.draftId);
      setDraftsList(res.drafts || []);
      setToast({ open: true, message: "Testimony draft saved successfully!", severity: "success" });
    } else {
      setToast({ open: true, message: res.message || "Failed to save draft", severity: "error" });
    }
  };

  const handleContinueDraft = (draft) => {
    if (!draft || !draft.data) return;
    const d = draft.data;
    if (d.title !== undefined) setTitle(d.title);
    if (d.description !== undefined) setRequestText(d.description);
    if (d.myPrayer !== undefined) setMyPrayer(d.myPrayer);
    setActiveDraftId(draft.id);
    setOpenDialog(true);
    setToast({ open: true, message: `Loaded draft: ${draft.title || 'Untitled'}`, severity: "info" });
  };

  const handleDeleteDraft = (draftId) => {
    const res = DraftManager.deleteDraft('testimonies', draftId);
    setDraftsList(res.drafts || []);
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft removed.", severity: "info" });
  };

  const handleArchive = (testimony) => {
    setConfirmDialog({
      open: true,
      title: "Archive Testimony",
      message: "Are you sure you want to archive this testimony? It will no longer be visible on the mobile app until restored.",
      onConfirm: () => {
        ArchiveManager.archiveItem('testimonies', testimony);
        setArchivedList(ArchiveManager.getArchivedItems('testimonies'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Testimony archived and hidden from mobile app.", severity: "info" });
      }
    });
  };

  const handleRestore = (testimonyId) => {
    ArchiveManager.restoreItem('testimonies', testimonyId);
    setArchivedList(ArchiveManager.getArchivedItems('testimonies'));
    setToast({ open: true, message: "Testimony restored to active status!", severity: "success" });
  };

  const handlePermanentDelete = (testimonyId) => {
    setConfirmDialog({
      open: true,
      title: "Permanently Delete Testimony",
      message: "Are you sure you want to permanently delete this archived testimony? This cannot be undone.",
      onConfirm: () => {
        ArchiveManager.deletePermanently('testimonies', testimonyId);
        setArchivedList(ArchiveManager.getArchivedItems('testimonies'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        setToast({ open: true, message: "Testimony permanently removed.", severity: "success" });
      }
    });
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleEdit = (testimony) => {
    setEditTestimonyId(testimony.id)
    setTitle(testimony.title)
    setDescription(testimony.description)
    setOpenEditDialog(true)

  };

  const handleDialogEditClose = () => {
    setOpenEditDialog(false);
    setFormError("");
  };

  const fetchCategories = async () => {
    // Replace this with your actual API call
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}all-prayer-categories?type=public`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

  }

    setCategories(data.data.categories);
    setOrganizations(data.data.organisations);
    setMyPrayers(data.data.prayerlist);

    SetActivePrayers(data.data.activetestimoniescount);
    SetPendingPrayers(data.data.pendingtestimoniescount);
    SetADeclinePrayers(data.data.rejectedtestimoniescount);


  };

  useEffect(() => {
    fetchPublicPrayerList();
  }, [page, filterCategory, filterSort, organization, fromDate, toDate]);

  const fetchPublicPrayerList = async () => {

    try {
      setLoading(true);
      // Replace this with your actual API call
      const token = localStorage.getItem('token');
      const targetOrg = effectiveOrgId || (organization?.id !== 0 ? organization?.id : null);
      const response = await fetch(`${API_URL}testimony-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page: page,
          category: filterCategory,
          status: filterSort,
          organization: targetOrg || null,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        })
      });
      const data = await response.json();


      if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

         }

      setPublicPrayerRequests(data.data);
      setTotalPages(data.pagination.totalPages);

    } catch (err) {
      // setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };







  // Form submit
  const handleSubmit = async (e) => {
    e.preventDefault();


    if (!myPrayer || !title.trim() || !requestText.trim()) {
      setFormError("Please select a prayer and title and enter your Testimony.");
      return;
    }


    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}add-testimony`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        PrayerId: myPrayer,
        title: title,
        description: requestText,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

    }
    
    if (data.status === 200) {
      fetchPublicPrayerList();
      setOpenDialog(false);
      window.location.reload();
    } else {
      setToast({ open: true, message: data.message, severity: "error" });
      setOpenDialog(false);
      fetchPublicPrayerList();
      window.location.reload();
    }


  };


  const handleUpdateSubmit = async (e) => {
    e.preventDefault();


    if (!title.trim() || !description.trim()) {
      setFormError("title and enter your testimony.");
      return;
    }


    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}update-testimony`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: editTestimonyId,
        title: title,
        description: description,
      }),
    });
    const data = await response.json();
    if (data.status === 200) {
      fetchPublicPrayerList();
      setOpenDialog(false);
      window.location.reload();
    } else {
      setToast({ open: true, message: data.message, severity: "error" });
      setOpenDialog(false);
      fetchPublicPrayerList();
      window.location.reload();
    }


  };


  // Modal open/close
  const handleDialogOpen = () => {
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
    setFormError("");
  };




  // Snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleViewPrayer = (req) => {
    setSelectedTestimony(req);
    setCategory(req.categories?.name || '');
    setPriority(req.importance || 'medium');
    setTitle(req.title || '');
    setDescription(req.description || '');
    setCreated_at(req.created_at || '');
    setOpenViewDialog(true);
  };
  const handleViewDialogClose = () => {
    setOpenViewDialog(false);
    setSelectedTestimony(null);
  };
  const handleApprovePrayer = async (id, status) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}approve-testimony`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: id,
        status: status,
      }),
    });
    const data = await response.json();
    if (data.status === 200) {
      fetchPublicPrayerList();
      setOpenDialog(false);

      console.log('Hi I am if condition');
    } else {
      setToast({ open: true, message: data.message, severity: "error" });
      window.location.reload();
    }


  };



  const handleStatCardClick = (targetStatus) => {
    if (filterSort === targetStatus) {
      setFilterSort("");
    } else {
      setFilterSort(targetStatus);
    }
    setPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Testimonies - GPN</title>
      </Helmet>
      <Box p={{ xs: 1, sm: 2 }}>
        {/* Compact 2-Row Unified Toolbar */}
        <Paper
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
            boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
          }}
        >
          {/* Row 1: Title, Count, Search, Sort & Main Actions */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.2,
              pb: 1,
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            {/* Left: Heading & Total */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: isLight ? "#0F172A" : "#FFFFFF", fontSize: "1rem" }}>
                Testimonies
              </Typography>
              <Chip
                size="small"
                label={`${ActivePrayers + PendingPrayers + DeclinePrayers} Total`}
                sx={{
                  bgcolor: "#E0F2FE",
                  color: "#0284C7",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: 22,
                }}
              />
            </Box>

            {/* Right: Search, Sort, Drafts, Add Testimony */}
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search testimonies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#64748B" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 200 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
              />

              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                displayEmpty
                renderValue={(val) => `Sort: ${val === "asc" ? "A → Z" : "Z → A"}`}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
              >
                <MenuItem value="asc">Name: A → Z</MenuItem>
                <MenuItem value="desc">Name: Z → A</MenuItem>
              </Select>

              <Button
                variant="outlined"
                size="small"
                startIcon={<DraftsIcon sx={{ fontSize: 18 }} />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('testimonies'));
                  setDraftsOpen(true);
                }}
                sx={{
                  py: 0.5,
                  px: 1.2,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  color: draftsList.length > 0 ? "#ffb74d" : "#64748B",
                  borderColor: draftsList.length > 0 ? "rgba(255, 183, 77, 0.6)" : "#CBD5E1",
                  "&:hover": { borderColor: "#ffb74d", bgcolor: "rgba(255, 183, 77, 0.08)" },
                }}
              >
                Drafts ({draftsList.length}/3)
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={handleDialogOpen}
                sx={{
                  background: "#177373",
                  py: 0.6,
                  px: 1.5,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  "&:hover": { background: "#115656" },
                }}
              >
                + Add Testimony
              </Button>
            </Box>
          </Box>

          {/* Row 2: Status Quick Chips + Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Status Quick Chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
              {[
                { key: "", label: "All", count: ActivePrayers + PendingPrayers + DeclinePrayers },
                { key: "1", label: "Active", count: ActivePrayers },
                { key: "0", label: "Pending", count: PendingPrayers },
                { key: "2", label: "Declined", count: DeclinePrayers },
              ].map((item) => {
                const isActive = filterActiveTab !== "archived" && filterSort === item.key;
                return (
                  <Chip
                    key={item.key || "all"}
                    size="small"
                    clickable
                    label={`${item.label} (${item.count})`}
                    onClick={() => { setFilterActiveTab("all"); handleStatCardClick(item.key); }}
                    sx={{
                      fontSize: "0.78rem",
                      fontWeight: isActive ? 700 : 500,
                      bgcolor: isActive ? "#0F766E" : "#F1F5F9",
                      color: isActive ? "#FFFFFF" : "#475569",
                      height: 26,
                      "&:hover": { bgcolor: isActive ? "#115E59" : "#E2E8F0" },
                    }}
                  />
                );
              })}

              <Chip
                size="small"
                clickable
                label={`Archived (${archivedList.length})`}
                onClick={() => {
                  setFilterActiveTab(filterActiveTab === "archived" ? "all" : "archived");
                  setPage(1);
                }}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: filterActiveTab === "archived" ? 700 : 500,
                  bgcolor: filterActiveTab === "archived" ? "#D97706" : "#FEF3C7",
                  color: filterActiveTab === "archived" ? "#FFFFFF" : "#92400E",
                  height: 26,
                  "&:hover": { bgcolor: filterActiveTab === "archived" ? "#B45309" : "#FDE68A" },
                }}
              />
            </Box>

            {/* Filter Dropdowns: Category, Dates, Org */}
            <Box sx={{ display: "flex", gap: 0.8, alignItems: "center", flexWrap: "wrap" }}>
              <Select
                size="small"
                value={filterCategory}
                onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
                displayEmpty
                renderValue={(val) => {
                  if (!val) return "All Categories";
                  const c = categories.find((x) => x.id === val);
                  return c ? c.name : "Category";
                }}
                sx={{ width: 140, "& .MuiSelect-select": { py: 0.5, fontSize: "0.8rem" } }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>

              <TextField
                size="small"
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              <TextField
                size="small"
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 130, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }}
              />

              {user?.role === 1 && !isSwitched && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} label="Org" sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: "0.8rem" } }} />}
                  sx={{ width: 170 }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {loading && <SectionLoader open={loading} />}

        {/* Requests - CSS Grid layout prevents single card stretching */}
        {(() => {
          const rawList = filterActiveTab === "archived"
            ? archivedList
            : publicPrayerRequests.filter(t => !archivedList.some(a => String(a.id) === String(t.id)));

          const displayedTestimonies = rawList.filter(t => {
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            const titleMatch = (t.title || '').toLowerCase().includes(q);
            const descMatch = (t.description || '').toLowerCase().includes(q);
            const nameMatch = `${t.app_users?.first_name || ''} ${t.app_users?.last_name || ''}`.toLowerCase().includes(q);
            return titleMatch || descMatch || nameMatch;
          });

          const sortedTestimonies = [...displayedTestimonies].sort((a, b) => {
            const nameA = (a.title || `${a.app_users?.first_name || ''} ${a.app_users?.last_name || ''}`).trim().toLowerCase();
            const nameB = (b.title || `${b.app_users?.first_name || ''} ${b.app_users?.last_name || ''}`).trim().toLowerCase();
            if (sortOrder === 'desc') return nameB.localeCompare(nameA);
            return nameA.localeCompare(nameB);
          });

          return (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.25)',
                bgcolor: isLight ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
                boxShadow: isLight ? '0 2px 10px rgba(0, 0, 0, 0.04)' : '0 4px 20px rgba(0, 0, 0, 0.25)',
                width: '100%',
              }}
            >
              <Table sx={{ minWidth: 800 }}>
                <TableHead sx={{ bgcolor: isLight ? '#F1F5F9' : '#1a2636' }}>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 800, width: 60, color: isLight ? '#475569' : '#E2E8F0' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 800, minWidth: 180, color: isLight ? '#475569' : '#E2E8F0' }}>Requester</TableCell>
                    <TableCell sx={{ fontWeight: 800, minWidth: 260, color: isLight ? '#475569' : '#E2E8F0' }}>Title & Testimony</TableCell>
                    <TableCell sx={{ fontWeight: 800, minWidth: 140, color: isLight ? '#475569' : '#E2E8F0' }}>Category</TableCell>
                    {user?.role === 1 && (
                      <TableCell sx={{ fontWeight: 800, minWidth: 160, color: isLight ? '#475569' : '#E2E8F0' }}>Organization</TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 800, minWidth: 130, color: isLight ? '#475569' : '#E2E8F0' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 800, minWidth: 120, color: isLight ? '#475569' : '#E2E8F0' }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, minWidth: 160, color: isLight ? '#475569' : '#E2E8F0' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTestimonies.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={user?.role === 1 ? 8 : 7} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          {filterActiveTab === "archived" ? "No archived testimonies found." : "No Testimonies found."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedTestimonies.map((req, idx) => {
                      const userName = `${req.app_users?.first_name || ''} ${req.app_users?.last_name || ''}`.trim() || 'Anonymous User';
                      const userEmail = req.app_users?.email || '';

                      return (
                        <TableRow
                          key={req.id || idx}
                          hover
                          onClick={() => handleViewPrayer(req)}
                          sx={{
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                              bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                            },
                            '& td': {
                              borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                              py: 1.5,
                              color: isLight ? '#1E293B' : '#F1F5F9',
                            }
                          }}
                        >
                          <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)', fontSize: '0.82rem' }}>
                            {((page - 1) * 10) + idx + 1}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.2}>
                              <Avatar sx={{ width: 36, height: 36, bgcolor: '#4CA1AF', fontWeight: 700, fontSize: '0.85rem' }}>
                                {userName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box minWidth={0}>
                                <Typography variant="body2" fontWeight={700} noWrap sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}>
                                  {userName}
                                </Typography>
                                {userEmail && (
                                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                                    {userEmail}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 280, color: isLight ? '#0F172A' : '#F8FAFC' }}>
                              {req.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              maxWidth: 320,
                              lineHeight: 1.3
                            }}>
                              {req.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={req.categories?.name || 'General'}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                bgcolor: isLight ? '#F1F5F9' : 'rgba(255,255,255,0.05)',
                              }}
                            />
                          </TableCell>
                          {user?.role === 1 && (
                            <TableCell>
                              {req.origanisation?.org_name ? (
                                <Chip
                                  label={req.origanisation.org_name}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                  sx={{ borderColor: '#4CA1AF', color: '#4CA1AF', fontWeight: 600, fontSize: '0.72rem' }}
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.6}>
                              <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {req.created_at ? new Date(req.created_at).toLocaleDateString() : '—'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                req.deleted_at != null
                                  ? 'Deleted'
                                  : req.status === 0
                                    ? "Pending"
                                    : req.status === 1
                                      ? "Approved"
                                      : req.status === 2
                                        ? "Declined"
                                        : "Deleted"
                              }
                              size="small"
                              color={
                                req.deleted_at != null
                                  ? 'error'
                                  : req.status === 0
                                    ? "warning"
                                    : req.status === 1
                                      ? "success"
                                      : req.status === 2
                                        ? "warning"
                                        : "error"
                              }
                              sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewPrayer(req)}
                                  aria-label="View"
                                  sx={{ color: '#4CA1AF', '&:hover': { background: 'rgba(76, 161, 175, 0.1)' } }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {req.deleted_at === null && (
                                <>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      onClick={() => handleEdit(req)}
                                      size="small"
                                      sx={{ color: '#4CA1AF', '&:hover': { background: 'rgba(76, 161, 175, 0.1)' } }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleApprovePrayer(req.id, 3)}
                                      aria-label="Delete"
                                      sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}

                              {req.status === 0 && req.deleted_at == null && (
                                <>
                                  <Tooltip title="Approve">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleApprovePrayer(req.id, 1)}
                                      aria-label="Approve"
                                      sx={{ color: '#10B981', '&:hover': { background: 'rgba(16, 185, 129, 0.1)' } }}
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Decline">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleApprovePrayer(req.id, 2)}
                                      aria-label="Decline"
                                      sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}

                              {filterActiveTab === "archived" ? (
                                <>
                                  <Tooltip title="Restore to active (Make visible in mobile app)">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRestore(req.id)}
                                      aria-label="Restore"
                                      sx={{ color: '#4CAF50', '&:hover': { background: 'rgba(76, 175, 80, 0.1)' } }}
                                    >
                                      <UnarchiveIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Permanently delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handlePermanentDelete(req.id)}
                                      aria-label="Delete Permanently"
                                      sx={{ color: '#EF4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                                    >
                                      <DeleteForeverIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : (
                                <Tooltip title="Archive (Hide from mobile app)">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleArchive(req)}
                                    aria-label="Archive"
                                    sx={{ color: '#ffb74d', '&:hover': { background: 'rgba(255, 183, 77, 0.1)' } }}
                                  >
                                    <ArchiveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}

        {/* Pagination */}

        {totalPages > 0 && (
          <Box display="flex" justifyContent="center" mt={3} width="100%">
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
            />
          </Box>
        )}


        {/* Submit Prayer Request Modal */}
        <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Add Testimony</span>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DraftsIcon />}
                onClick={() => {
                  setDraftsList(DraftManager.getDrafts('testimonies'));
                  setDraftsOpen(true);
                }}
                sx={{
                  borderColor: 'rgba(255, 183, 77, 0.5)',
                  color: '#ffb74d',
                  fontSize: '0.75rem',
                }}
              >
                Drafts ({draftsList.length}/3)
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleManualSaveDraft}
                sx={{
                  borderColor: 'rgba(76, 161, 175, 0.5)',
                  color: '#64d2e2',
                  fontSize: '0.75rem',
                }}
              >
                Save Draft
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <form id="prayer-form" onSubmit={(e) => {
              if (activeDraftId) {
                DraftManager.deleteDraft('testimonies', activeDraftId);
                setActiveDraftId(null);
                setDraftsList(DraftManager.getDrafts('testimonies'));
              }
              handleSubmit(e);
            }}>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <FormControl fullWidth margin="dense" size="small">
                  <InputLabel>Prayers</InputLabel>
                  <Select
                    value={myPrayer}
                    label="Prayer"
                    onChange={(e) => setMyPrayer(e.target.value)}
                    data-testid="prayer-select"
                  >
                    {myPrayers.map((prp) => (
                      <MenuItem key={prp.id} value={prp.id}>
                        {prp.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <HelpTooltip
                  title="Related Prayer"
                  purpose="Connects this testimony directly to an existing answered prayer"
                  howItWorks="Links the testimony to praise reports in prayer chains"
                  mobileEffect="Shows as an answered praise update for users who prayed"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <TextField
                  label="Title"
                  fullWidth
                  margin="dense"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <HelpTooltip
                  title="Testimony Headline"
                  purpose="Short inspiring title of what God did"
                  howItWorks="Shown at the top of the praise card on mobile"
                  mobileEffect="Prominently featured in community feed"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                <TextField
                  label="Testimony"
                  multiline
                  rows={4}
                  fullWidth
                  margin="dense"
                  value={requestText}
                  onChange={(e) => setRequestText(e.target.value)}
                  error={!!formError}
                  helperText={formError}
                />
                <Box sx={{ pt: 1 }}>
                  <HelpTooltip
                    title="Testimony Story"
                    purpose="Full story of how the prayer was answered"
                    howItWorks="Encourages and builds faith in the global prayer community"
                    mobileEffect="Users read the praise report and react with prayers and gratitude"
                  />
                </Box>
              </Box>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button type="submit" form="prayer-form" variant="contained" color="primary">
              Submit Testimony
            </Button>
          </DialogActions>
        </Dialog>


        <Dialog
          open={openViewDialog}
          onClose={handleViewDialogClose}
          fullWidth
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: isLight ? '#FFFFFF' : '#1e293b',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.45)',
            }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)',
              color: '#FFFFFF',
              py: 2.2,
              px: 3,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <VolunteerActivismIcon sx={{ fontSize: 28, color: '#FFFFFF' }} />
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#FFFFFF', lineHeight: 1.2 }}>
                  Testimony Details
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  Praise report & answered prayer record
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleViewDialogClose} sx={{ color: '#FFFFFF', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            {selectedTestimony && (
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Requester Profile Card */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.04)',
                    border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <Avatar
                    sx={{
                      width: 52,
                      height: 52,
                      bgcolor: '#0D9488',
                      fontWeight: 800,
                      fontSize: '1.25rem',
                    }}
                  >
                    {((selectedTestimony.app_users?.first_name || 'U').charAt(0)).toUpperCase()}
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}>
                      {`${selectedTestimony.app_users?.first_name || ''} ${selectedTestimony.app_users?.last_name || ''}`.trim() || 'Anonymous User'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {selectedTestimony.app_users?.email || 'No email provided'}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.8}>
                    <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {selectedTestimony.created_at ? new Date(selectedTestimony.created_at).toLocaleString() : '—'}
                    </Typography>
                  </Box>
                </Box>

                {/* Metadata Badges */}
                <Box display="flex" flexWrap="wrap" gap={1}>
                  <Chip
                    label={`Category: ${selectedTestimony.categories?.name || category || 'General'}`}
                    sx={{
                      fontWeight: 700,
                      bgcolor: isLight ? '#CCFBF1' : 'rgba(13, 148, 136, 0.25)',
                      color: isLight ? '#0F766E' : '#2DD4BF',
                    }}
                  />
                  <Chip
                    label={
                      selectedTestimony.deleted_at != null
                        ? 'Deleted'
                        : selectedTestimony.status === 0
                          ? "Pending Review"
                          : selectedTestimony.status === 1
                            ? "Approved"
                            : selectedTestimony.status === 2
                              ? "Declined"
                              : "Deleted"
                    }
                    color={
                      selectedTestimony.deleted_at != null
                        ? 'error'
                        : selectedTestimony.status === 0
                          ? "warning"
                          : selectedTestimony.status === 1
                            ? "success"
                            : selectedTestimony.status === 2
                              ? "warning"
                              : "error"
                    }
                    sx={{ fontWeight: 700 }}
                  />
                  {selectedTestimony.origanisation?.org_name && (
                    <Chip
                      label={`Org: ${selectedTestimony.origanisation.org_name}`}
                      variant="outlined"
                      sx={{ fontWeight: 600, borderColor: '#0D9488', color: isLight ? '#0F766E' : '#2DD4BF' }}
                    />
                  )}
                </Box>

                {/* Testimony Title & Content */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2.5,
                    bgcolor: isLight ? '#FFFFFF' : 'rgba(255, 255, 255, 0.02)',
                    border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    gutterBottom
                    sx={{ color: isLight ? '#0F172A' : '#F8FAFC' }}
                  >
                    {selectedTestimony.title || title}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="body1"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.8,
                      fontSize: '1rem',
                      color: isLight ? '#334155' : '#CBD5E1',
                    }}
                  >
                    {selectedTestimony.description || description}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2.5, px: 3, borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.08)', gap: 1 }}>
            {selectedTestimony && selectedTestimony.status === 0 && selectedTestimony.deleted_at == null && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => {
                    handleApprovePrayer(selectedTestimony.id, 1);
                    handleViewDialogClose();
                  }}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    handleApprovePrayer(selectedTestimony.id, 2);
                    handleViewDialogClose();
                  }}
                >
                  Decline
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              onClick={handleViewDialogClose}
              sx={{ ml: 'auto' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>


        {/* Submission Feedback Snackbar */}
        <Toast toast={toast} setToast={setToast} />





        <Dialog open={openEditDialog} onClose={handleDialogEditClose} fullWidth maxWidth="sm">
          <DialogTitle>Edit Testimony</DialogTitle>
          <DialogContent>
            <form id="prayer-form" onSubmit={handleUpdateSubmit}>
              <TextField
                label="Title"
                fullWidth
                margin="dense"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />


              <TextField
                label="Testimony"
                multiline
                rows={4}
                fullWidth
                margin="dense"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                error={!!formError}
                helperText={formError}
              />


            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogEditClose}>Cancel</Button>
            <Button type="submit" form="prayer-form" variant="contained" color="primary">
              Update
            </Button>
          </DialogActions>
        </Dialog>



      </Box>

      {/* Floating Settings icon at bottom-right */}
      <Tooltip title="Testimonies Settings" placement="left">
        <Fab
          size="medium"
          onClick={() => setSettingsOpen(true)}
          sx={{
            position: "fixed",
            bottom: 28,
            right: 28,
            bgcolor: "#4CA1AF",
            color: "#FFFFFF",
            boxShadow: "0 6px 20px rgba(0, 0, 0, 0.4)",
            "&:hover": {
              bgcolor: "#3A8A97",
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease-in-out",
            zIndex: 1100,
          }}
        >
          <SettingsIcon />
        </Fab>
      </Tooltip>

      <ModuleSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        moduleKey="testimony"
        moduleTitle="Testimonies"
      />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity="error"
      />
      <DraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        moduleKey="testimonies"
        moduleTitle="Testimonies"
        onContinueDraft={handleContinueDraft}
      />
    </>
  );
}
